<template>
  <div class="diff-viewer">
    <div v-if="!hasChanges" class="no-changes">
      <span class="text-muted">No visible changes</span>
    </div>
    <div v-else class="diff-container">
      <!-- Side-by-side view for JSON/text -->
      <div v-if="viewMode === 'side-by-side'" class="diff-side-by-side">
        <div class="diff-panel diff-old">
          <div class="diff-panel-header">
            <span class="badge badge-danger">Old Value</span>
          </div>
          <pre class="diff-content">{{ formattedOldValue }}</pre>
        </div>
        <div class="diff-panel diff-new">
          <div class="diff-panel-header">
            <span class="badge badge-success">New Value</span>
          </div>
          <pre class="diff-content">{{ formattedNewValue }}</pre>
        </div>
      </div>
      
      <!-- Unified diff view -->
      <div v-else class="diff-unified">
        <!-- Inline mode for single-line word diffs -->
        <div v-if="isInlineMode" class="diff-inline">
          <span
            v-for="(part, index) in diffParts"
            :key="index"
            :class="getInlineDiffClass(part)"
          >{{ part.value }}</span>
        </div>
        <!-- Line mode for multiline diffs -->
        <div v-else class="diff-lines">
          <div
            v-for="(part, index) in diffParts"
            :key="index"
            :class="getDiffPartClass(part)"
            class="diff-line"
          >
            <span class="diff-indicator">{{ getDiffIndicator(part) }}</span>
            <pre class="diff-text">{{ part.value }}</pre>
          </div>
        </div>
      </div>
      
      <!-- View mode toggle -->
      <div class="diff-controls">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :class="{ active: viewMode === 'unified' }"
          @click="viewMode = 'unified'"
        >
          Unified
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :class="{ active: viewMode === 'side-by-side' }"
          @click="viewMode = 'side-by-side'"
        >
          Side by Side
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as Diff from 'diff';

export default {
  name: 'DiffViewer',
  props: {
    oldValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null
    },
    newValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null
    },
    type: {
      type: String,
      default: 'string',
      validator: (value) => ['json', 'string', 'number', 'boolean', 'null', 'addition', 'deletion'].includes(value)
    }
  },
  data() {
    return {
      viewMode: 'unified'
    };
  },
  computed: {
    formattedOldValue() {
      return this.formatValue(this.oldValue);
    },
    formattedNewValue() {
      return this.formatValue(this.newValue);
    },
    hasChanges() {
      return this.formattedOldValue !== this.formattedNewValue;
    },
    diffParts() {
      if (!this.hasChanges) return [];
      
      const oldStr = this.formattedOldValue || '';
      const newStr = this.formattedNewValue || '';
      
      // Use different diff strategies based on content type
      if (this.type === 'json' || this.isMultiline(oldStr) || this.isMultiline(newStr)) {
        return Diff.diffLines(oldStr, newStr);
      }
      
      // Use diffChars for strings without spaces (single words/tokens)
      // Use diffWords for strings with spaces (sentences/phrases)
      const hasSpaces = oldStr.includes(' ') || newStr.includes(' ');
      if (hasSpaces) {
        return Diff.diffWords(oldStr, newStr);
      }
      
      return Diff.diffChars(oldStr, newStr);
    },
    isInlineMode() {
      // Use inline mode for single-line string changes (word-level diff)
      const oldStr = this.formattedOldValue || '';
      const newStr = this.formattedNewValue || '';
      return this.type !== 'json' && !this.isMultiline(oldStr) && !this.isMultiline(newStr);
    }
  },
  methods: {
    formatValue(value) {
      if (value === null || value === undefined) {
        return '(empty)';
      }
      
      if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
      }
      
      if (typeof value === 'number') {
        return String(value);
      }
      
      if (typeof value === 'string') {
        // Try to parse and prettify JSON
        if (this.type === 'json') {
          try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
          } catch (e) {
            return value;
          }
        }
        return value;
      }
      
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      
      return String(value);
    },
    isMultiline(str) {
      return str && str.includes('\n');
    },
    getDiffPartClass(part) {
      if (part.added) return 'diff-added';
      if (part.removed) return 'diff-removed';
      return 'diff-unchanged';
    },
    getDiffIndicator(part) {
      if (part.added) return '+';
      if (part.removed) return '-';
      return ' ';
    },
    getInlineDiffClass(part) {
      if (part.added) return 'inline-added';
      if (part.removed) return 'inline-removed';
      return 'inline-unchanged';
    }
  }
};
</script>

<style scoped>
.diff-viewer {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #1e2430;
}

.no-changes {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
}

.diff-container {
  position: relative;
}

/* Unified diff view */
.diff-unified {
  max-height: 400px;
  overflow-y: auto;
}

.diff-lines {
  padding: 0.5rem 0;
}

.diff-line {
  display: flex;
  align-items: flex-start;
  padding: 2px 0.75rem;
  min-height: 1.5em;
}

.diff-indicator {
  flex-shrink: 0;
  width: 1.5em;
  text-align: center;
  font-weight: bold;
  user-select: none;
}

.diff-text {
  margin: 0;
  padding: 0;
  white-space: pre-wrap;
  word-break: break-word;
  flex: 1;
  background: transparent;
  font-family: inherit;
  font-size: inherit;
}

.diff-added {
  background-color: rgba(46, 160, 67, 0.25);
  color: #7ee787;
}

.diff-added .diff-indicator {
  color: #3fb950;
}

.diff-removed {
  background-color: rgba(248, 81, 73, 0.25);
  color: #ffa198;
}

.diff-removed .diff-indicator {
  color: #f85149;
}

.diff-unchanged {
  color: #8b949e;
}

/* Inline diff view for single-line strings */
.diff-inline {
  padding: 0.75rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.inline-added {
  background-color: rgba(46, 160, 67, 0.35);
  color: #7ee787;
  padding: 0.1em 0.2em;
  border-radius: 3px;
  text-decoration: none;
}

.inline-removed {
  background-color: rgba(248, 81, 73, 0.35);
  color: #ffa198;
  padding: 0.1em 0.2em;
  border-radius: 3px;
  text-decoration: line-through;
}

.inline-unchanged {
  color: #c9d1d9;
}

/* Side-by-side view */
.diff-side-by-side {
  display: flex;
  gap: 1px;
  background-color: #30363d;
}

.diff-panel {
  flex: 1;
  min-width: 0;
  background-color: #1e2430;
}

.diff-panel-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #30363d;
}

.diff-old .diff-panel-header {
  background-color: rgba(248, 81, 73, 0.1);
}

.diff-new .diff-panel-header {
  background-color: rgba(46, 160, 67, 0.1);
}

.diff-content {
  margin: 0;
  padding: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 350px;
  overflow-y: auto;
  color: #c9d1d9;
  background: transparent;
  font-family: inherit;
  font-size: inherit;
}

.diff-old .diff-content {
  background-color: rgba(248, 81, 73, 0.08);
}

.diff-new .diff-content {
  background-color: rgba(46, 160, 67, 0.08);
}

/* Controls */
.diff-controls {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #30363d;
  background-color: #161b22;
}

.diff-controls .btn {
  font-size: 11px;
  padding: 0.25rem 0.5rem;
}

.diff-controls .btn.active {
  background-color: #238636;
  border-color: #238636;
  color: white;
}

/* Badge overrides */
.badge-danger {
  background-color: #da3633;
}

.badge-success {
  background-color: #238636;
}
</style>

